import { Stack, Box, Icons, NoData } from '@cast/design-system';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';

import { useInventoryWorkloadsMetrics } from '../_hooks/useInventoryWorkloadsMetrics';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { ResourceImpactChartDataKey } from './_components/_types';

import { ImpactAvgMetrics } from './_components/ImpactAvgMetrics';
import { ImpactChart } from './_components/ImpactChart';

export const MemoryImpactChart = () => {
  const { timeZone } = useInventoryWorkloadsTimeRangeSelection();
  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<ResourceImpactChartDataKey>();

  const {
    dataOrFixture: impactMetrics,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useInventoryWorkloadsMetrics();

  const hasError = !!error;
  const isChartLoading = isLoading || (hasError && isFetching);
  const memoryData = impactMetrics?.memory;
  const showEmptyState =
    !isChartLoading && !hasError && (memoryData?.length ?? 0) === 0;

  return (
    <WidgetLayout
      title="Autoscaling: Memory impact"
      testId="memory-impact-chart"
    >
      {hasError && !isFetching ? (
        <FailedToLoad title="Failed to load memory impact" refresh={refetch} />
      ) : showEmptyState ? (
        <NoData
          title="No data found"
          body="Try selecting a different time period."
          icon={<Icons.Question weight="fill" />}
          testId="memory-impact-chart-empty"
        />
      ) : (
        <Stack gap={12} height="100%">
          <ImpactAvgMetrics
            data={memoryData}
            unit="GiB"
            toggleMetric={toggleMetric}
            isMetricDisabled={isMetricDisabled}
            isLoading={isChartLoading}
            testId="memory-impact-chart-avg-metrics"
          />

          <Box flex={1} display="flex">
            <ImpactChart
              data={memoryData ?? []}
              isLoading={isChartLoading}
              unit="GiB"
              isMetricDisabled={isMetricDisabled}
              timeZone={timeZone}
            />
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
